import { useHistory, useParams } from 'react-router-dom';
import React, { useMemo, useState } from 'react';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import usePagination from '../../../utils/usePagination';
import {
  OrderBy, StudentCustomClassGroupSortType,
  useDeleteGroupMutation,
  useEnrollStudentsInGroupMutation,
  useGroupDetailsQuery,
  useGroupStudentListQuery,
  useRemoveInProgressSubmissionsOfSectionMutation,
  useStudentsWithInProgressSubmissionQuery,
} from '../../../generated/graphql';
import {
  PAGE_SIZE,
  USER_GROUP_MAPPING_ERROR,
  USER_GROUP_MAPPING_MESSAGE,
} from '../../../variables/constant';
import { openSnackbar } from '../../../components/Notifier';
import { NotifierType } from '../../../variables/types';
import getErrorMessage from '../../../utils/getErrorMessage';

interface RouteProp {
  id: string
}

const useManageGroup = () => {
  const pagination = usePagination('availablePage');
  const history = useHistory();
  const paginationAssignedStudents = usePagination('allocatedPage');
  const { id } = useParams<RouteProp>();

  const showError = (error: any) => {
    if (getErrorMessage(error) === USER_GROUP_MAPPING_ERROR) {
      openSnackbar({
        message: USER_GROUP_MAPPING_MESSAGE,
      }, NotifierType.Error);
      history.push('/groups');
    } else {
      openSnackbar({ message: getErrorMessage(error) }, NotifierType.Error);
    }
  };

  const { data, loading: groupDataLoading, error: gqlError } = useGroupDetailsQuery({
    fetchPolicy: 'network-only',
    variables: {
      id,
    },
  });

  if (gqlError) {
    showError(gqlError);
  }

  const [search, setSearch] = useQueryParam('search', StringParam);
  const [selectedAllocatedStudents, setSelectedAllocatedStudents] = useState<string[]>([]);
  const [selectedAvailableStudents, setSelectedAvailableStudents] = useState<string[]>([]);
  const [selectAllAvailable, setSelectAllAvailable] = useState(false);
  const [selectAllAllocated, setSelectAllAllocated] = useState(false);
  const [showRemoveSelectedConfirmation, setShowRemoveSelectedConfirmation] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [sortAllocated, setSortAllocated] = useQueryParam('sortAllocated', withDefault(StringParam, StudentCustomClassGroupSortType.Name));
  const [sortAvailable, setSortAvailable] = useQueryParam('sortAllocated', withDefault(StringParam, StudentCustomClassGroupSortType.Name));
  const [orderByAllocated, setOrderAllocated] = useQueryParam('orderAllocated', withDefault(StringParam, OrderBy.Asc));
  const [orderByAvailable, setOrderAvailable] = useQueryParam('orderAvailable', withDefault(StringParam, OrderBy.Asc));

  const { data: students, loading: studentsLoading, refetch } = useGroupStudentListQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-and-network',
    variables: {
      input: {
        group_id: id,
        ...(search ? { search } : {}),
        available_limit: PAGE_SIZE,
        available_page: pagination.page,
        allocated_limit: PAGE_SIZE,
        allocated_page: paginationAssignedStudents.page,
        allocated_sort: sortAllocated as StudentCustomClassGroupSortType,
        available_sort: sortAvailable as StudentCustomClassGroupSortType,
        allocated_orderBy: orderByAllocated as OrderBy,
        available_orderBy: orderByAvailable as OrderBy,
      },
    },
  });

  const [updateStudents, { loading: updateStudentsLoader }] = useEnrollStudentsInGroupMutation();

  const [removeStudentsAssignments, { loading: removeStudentsAssignmentsLoader }] = useRemoveInProgressSubmissionsOfSectionMutation();

  const [deleteGroup, { loading: deletingGroupLoader }] = useDeleteGroupMutation();

  const getStudents = useMemo(() => ({
    allocatedStudents: students?.studentGroupStudentsList?.allocated_students?.nodes ?? [],
    allocatedTotalCount: students?.studentGroupStudentsList?.allocated_students?.totalCount ?? 0,
    availableStudents: students?.studentGroupStudentsList?.available_students?.nodes,
    availableTotalCount: students?.studentGroupStudentsList?.available_students?.totalCount,
    availableStudentIDs: students?.studentGroupStudentsList?.available_student_ids ?? [],
    allocatedStudentIDs: students?.studentGroupStudentsList?.allocated_student_ids ?? [],
  }), [students]);

  const {
    allocatedStudents,
    allocatedTotalCount,
    availableStudents,
    availableTotalCount,
    availableStudentIDs,
    allocatedStudentIDs,
  } = getStudents;

  const { data: studentsWithSubmission, loading: studentsWithSubmissionLoader, error: inProgressGqlErr } = useStudentsWithInProgressSubmissionQuery({
    fetchPolicy: 'no-cache',
    canonizeResults: false,
    variables: {
      sectionID: id,
      studentIDs: showRemoveSelectedConfirmation
        ? Array.from(new Set(selectedAllocatedStudents))
        : Array.from(new Set(allocatedStudentIDs)),
    },
    skip: !showRemoveSelectedConfirmation && !deleteConfirmation,
  });

  if (inProgressGqlErr) {
    showError(inProgressGqlErr);
  }

  const onChangeAllocated = (studentId: string) => {
    if (selectAllAllocated) {
      setSelectAllAllocated(false);
    }
    if (selectedAllocatedStudents.includes(studentId)) {
      setSelectedAllocatedStudents(selectedAllocatedStudents.filter((item) => item !== studentId));
    } else {
      setSelectedAllocatedStudents([...selectedAllocatedStudents, studentId]);
    }
  };

  const onChangeAvailable = (studentId: string) => {
    if (selectAllAvailable) {
      setSelectAllAvailable(false);
    }
    if (selectedAvailableStudents.includes(studentId)) {
      setSelectedAvailableStudents(selectedAvailableStudents.filter((item) => item !== studentId));
    } else {
      setSelectedAvailableStudents([...selectedAvailableStudents, studentId]);
    }
  };

  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    pagination.setPage(1);
    paginationAssignedStudents.setPage(1);
    if (event.target.value === '') {
      setSearch(undefined);
    } else {
      setSearch(event.target.value);
    }
    setSelectAllAllocated(false);
    setSelectAllAvailable(false);
  };

  const isAllocatedSelected = (studentId: string) => selectedAllocatedStudents.includes(studentId);

  const isAvailableSelected = (studentId: string) => selectedAvailableStudents.includes(studentId);

  const handlePageChange = (
    _: React.ChangeEvent<unknown>,
    value: React.SetStateAction<number>,
  ) => {
    pagination.setPage(value as number);
  };

  const handleAssignedStudentsPageChange = (
    _: React.ChangeEvent<unknown>,
    value: React.SetStateAction<number>,
  ) => {
    paginationAssignedStudents.setPage(value as number);
  };

  const handleSelectAllAvailable = () => {
    if (selectAllAvailable) {
      setSelectedAvailableStudents([]);
    } else {
      setSelectedAvailableStudents([...selectedAvailableStudents, ...availableStudentIDs]);
    }
    setSelectAllAvailable(!selectAllAvailable);
  };

  const handleSelectAllAllocated = () => {
    if (selectAllAllocated) {
      setSelectedAllocatedStudents([]);
    } else {
      setSelectedAllocatedStudents([...selectedAllocatedStudents, ...allocatedStudentIDs]);
    }
    setSelectAllAllocated(!selectAllAllocated);
  };

  const update = async (studentIDsToAdd: string[], studentIDsToRemove: string[]) => {
    try {
      const response = await updateStudents({
        variables: {
          input: {
            selected_section_id: id,
            ...(studentIDsToAdd?.length > 0 ? { student_ids_to_add: Array.from(new Set(studentIDsToAdd)) } : {}),
            ...(studentIDsToRemove?.length > 0 ? { student_ids_to_remove: Array.from(new Set(studentIDsToRemove)) } : {}),
          },
        },
      });
      paginationAssignedStudents.setPage(1);
      pagination.setPage(1);
      if (response?.data?.enrollStudentsInGroup) {
        openSnackbar({ message: 'Group updated successfully.' }, NotifierType.Success);
        await refetch();
        setSelectAllAllocated(false);
        setSelectAllAvailable(false);
        if (studentIDsToAdd?.length > 0) {
          setSelectedAvailableStudents([]);
        } else if (studentIDsToRemove?.length > 0) {
          setSelectedAllocatedStudents([]);
        }
      } else {
        openSnackbar({ message: 'Error updating the group.' }, NotifierType.Error);
      }
    } catch (error) {
      showError(error);
    }
  };

  const handleAddSelected = async () => {
    await update(selectedAvailableStudents, []);
  };

  const handleRemoveSelected = async () => {
    setShowRemoveSelectedConfirmation(true);
  };

  const handleRemoveSelectedConfirm = async () => {
    setShowRemoveSelectedConfirmation(false);
    if (studentsWithSubmission?.studentsWithInProgressSubmission && studentsWithSubmission?.studentsWithInProgressSubmission?.length > 0) {
      try {
        const response = await removeStudentsAssignments({
          variables: {
            sectionID: id,
            studentIDs: Array.from(new Set(selectedAllocatedStudents)),
          },
        });
        if (response?.data?.removeInProgressSubmissionsOfSection) {
          await update([], selectedAllocatedStudents);
        } else {
          openSnackbar({ message: 'Error in removing in progress PTs.' }, NotifierType.Success);
        }
      } catch (error) {
        openSnackbar({ message: getErrorMessage(error) }, NotifierType.Error);
      }
    } else {
      await update([], selectedAllocatedStudents);
    }
  };

  const confirmationMessage = useMemo(() => {
    const studentSubmissions = studentsWithSubmission?.studentsWithInProgressSubmission ?? [];
    return studentSubmissions?.length > 0
      ? `${studentSubmissions?.reduce((prevValue, current, index) => {
        if (index === 0) {
          return `${current?.first_name} ${current?.last_name}`;
        } if (index === studentSubmissions.length - 1) {
          return `${prevValue} and ${current?.first_name} ${current?.last_name}`;
        }
        return `${prevValue}, ${current?.first_name} ${current?.last_name}`;
      }, '')} have PTs in progress. If you proceed, their progress will be lost.`
      : deleteConfirmation ? 'Are you sure you want to delete this group?' : 'Are you sure you want to remove the selected students?';
  }, [deleteConfirmation, studentsWithSubmission]);

  const cancelRemoveSelected = () => {
    setShowRemoveSelectedConfirmation(false);
  };

  const handleDelete = async () => {
    setDeleteConfirmation(true);
  };

  const handleAllocatedSort = (column: string) => () => {
    if (column === sortAllocated) {
      setOrderAllocated(orderByAllocated === OrderBy.Desc ? OrderBy.Asc : OrderBy.Desc);
    } else {
      setSortAllocated(column);
      setOrderAllocated(OrderBy.Asc);
    }
  };

  const handleAvailableSort = (column: string) => () => {
    if (column === sortAvailable) {
      setOrderAvailable(orderByAvailable === OrderBy.Desc ? OrderBy.Asc : OrderBy.Desc);
    } else {
      setSortAvailable(column);
      setOrderAvailable(OrderBy.Asc);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleteConfirmation(false);
    try {
      await deleteGroup({
        variables: {
          group_id: id,
        },
      });
      history.push('/groups');
      openSnackbar({
        message: 'Group deleted successfully',
      }, NotifierType.Success);
    } catch (err) {
      showError(err);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation(false);
  };

  const groupData = data?.groupDetails;

  return {
    loading: groupDataLoading || studentsLoading || updateStudentsLoader,
    pageLoading: studentsWithSubmissionLoader || removeStudentsAssignmentsLoader || deletingGroupLoader,
    groupData,
    pagination,
    handlePageChange,
    paginationAssignedStudents,
    handleAssignedStudentsPageChange,
    allocatedStudents,
    allocatedTotalCount,
    availableStudents,
    availableTotalCount,
    onChangeAllocated,
    onChangeAvailable,
    isAllocatedSelected,
    isAvailableSelected,
    selectedAllocatedStudents,
    selectedAvailableStudents,
    selectAllAvailable,
    selectAllAllocated,
    handleSelectAllAvailable,
    handleSelectAllAllocated,
    refetch,
    handleAddSelected,
    handleRemoveSelected,
    handleRemoveSelectedConfirm,
    confirmationMessage,
    cancelRemoveSelected,
    showRemoveSelectedConfirmation,
    search,
    onSearch,
    deleteConfirmation,
    handleDelete,
    handleDeleteConfirm,
    cancelDelete,
    handleAllocatedSort,
    handleAvailableSort,
    sortAllocated,
    orderByAllocated,
    sortAvailable,
    orderByAvailable,
  };
};

export default useManageGroup;
